import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import {
  getAssociacoes,
  removeAssociacao,
  type Associacao,
} from '../lib/associacoesStorage';
import { getAgencias, getGuias } from '../lib/guiasAgenciasStorage';
import { getCidadeById } from '../lib/experienciasStorage';
import { syncParceirosToApi } from '../lib/parceirosApi';
import { useRefetchWhenCatalogChanges } from '../contexts/AdminCatalogContext';
import type { StatusCadastro } from '../lib/guiasAgenciasStorage';

const statusLabel: Record<StatusCadastro, string> = {
  pendente: 'Em análise',
  aprovado: 'Aprovado',
  recusado: 'Recusado',
  inativo: 'Inativo',
};

const acoesIconBtnClass =
  'inline-flex items-center justify-center p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors shrink-0';

function IconPencil() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
      />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}

function statusBadgeClass(s: StatusCadastro): string {
  if (s === 'aprovado') return 'bg-green-100 text-green-800';
  if (s === 'recusado') return 'bg-red-100 text-red-800';
  if (s === 'inativo') return 'bg-gray-200 text-gray-700';
  return 'bg-amber-100 text-amber-800';
}

function truncar(texto: string, max: number): string {
  const t = texto.trim();
  if (t.length <= max) return t || '—';
  return `${t.slice(0, max)}…`;
}

export default function AssociacaoPage() {
  const [associacoes, setAssociacoes] = useState<Associacao[]>([]);
  const [tick, setTick] = useState(0);

  useRefetchWhenCatalogChanges(() => setTick((n) => n + 1));

  useEffect(() => {
    setAssociacoes(getAssociacoes());
  }, [tick]);

  const ordenadas = useMemo(
    () =>
      [...associacoes].sort((a, b) =>
        (a.razaoSocial || '').localeCompare(b.razaoSocial || '', 'pt-BR', { sensitivity: 'base' })
      ),
    [associacoes]
  );

  const handleExcluir = (a: Associacao) => {
    const nGuias = a.guiaIds?.length ?? 0;
    const msg =
      nGuias > 0
        ? `Excluir "${a.razaoSocial}"? ${nGuias} guia(s) deixam de estar vinculados a esta associação.`
        : `Excluir "${a.razaoSocial}"?`;
    if (!confirm(msg)) return;
    removeAssociacao(a.id);
    setAssociacoes(getAssociacoes());
    void syncParceirosToApi(getGuias(), getAgencias(), getAssociacoes());
  };

  return (
    <AdminLayout>
      <div className="w-full min-w-0 px-4 sm:px-6 lg:px-8 py-6">
        <nav className="text-sm text-gray-500 mb-2">
          <Link to="/dashboard" className="hover:text-gray-700">
            Home
          </Link>
          {' > '}
          <span className="text-gray-800">Associações</span>
        </nav>

        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Associações</h1>
            <p className="text-sm text-gray-600 mt-1 max-w-2xl">
              Lista das associações cadastradas. Vincule-as aos guias em{' '}
              <Link to="/guias" className="text-orange-600 font-medium hover:underline">
                Parceiros
              </Link>
              .
            </p>
          </div>
          <div className="flex shrink-0 sm:justify-end">
            <Link
              to="/associacao/nova"
              className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 shadow-sm"
            >
              Criar associação
            </Link>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm text-left">
              <thead className="bg-gray-50 text-gray-600 border-b border-gray-200">
                <tr>
                  <th className="py-3 px-4 font-semibold">Razão social</th>
                  <th className="py-3 px-4 font-semibold">E-mail</th>
                  <th className="py-3 px-4 font-semibold">Telefone</th>
                  <th className="py-3 px-4 font-semibold whitespace-nowrap">CNPJ</th>
                  <th className="py-3 px-4 font-semibold">Cidade</th>
                  <th className="py-3 px-4 font-semibold">Endereço</th>
                  <th className="py-3 px-4 font-semibold">Estado</th>
                  <th className="py-3 px-4 font-semibold text-right w-[1%] whitespace-nowrap">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ordenadas.map((a) => {
                  const c = a.cidadeId ? getCidadeById(a.cidadeId) : null;
                  const linhaEnd = [a.enderecoLogradouro, a.enderecoNumero].filter(Boolean).join(', ');
                  return (
                    <tr key={a.id} className="hover:bg-gray-50/80">
                      <td className="py-3 px-4 text-gray-900 font-medium">{a.razaoSocial}</td>
                      <td className="py-3 px-4 text-gray-700 break-all max-w-[200px]">{a.email || '—'}</td>
                      <td className="py-3 px-4 text-gray-600 whitespace-nowrap">{a.telefone || '—'}</td>
                      <td className="py-3 px-4 text-gray-600 whitespace-nowrap">{a.cnpj || '—'}</td>
                      <td className="py-3 px-4 text-gray-600">{c?.cidade ?? '—'}</td>
                      <td className="py-3 px-4 text-gray-600 max-w-[240px]" title={a.enderecoCompleto}>
                        {truncar(linhaEnd || a.enderecoCompleto, 42)}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${statusBadgeClass(a.status)}`}
                        >
                          {statusLabel[a.status]}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-1">
                          <Link
                            to={`/associacao/${a.id}/editar`}
                            className={`${acoesIconBtnClass} hover:text-orange-600`}
                            title="Editar"
                            aria-label={`Editar ${a.razaoSocial}`}
                          >
                            <IconPencil />
                          </Link>
                          <button
                            type="button"
                            onClick={() => handleExcluir(a)}
                            className={`${acoesIconBtnClass} hover:text-red-600`}
                            title="Excluir"
                            aria-label={`Excluir ${a.razaoSocial}`}
                          >
                            <IconTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {ordenadas.length === 0 && (
            <div className="px-6 py-12 text-center text-gray-500 text-sm">
              <p className="mb-4">Ainda não há associações cadastradas.</p>
              <Link
                to="/associacao/nova"
                className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600"
              >
                Criar primeira associação
              </Link>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
